import React from "react";

import Modal from "react-bootstrap/Modal";
import PropTypes from "prop-types";
import moment from "moment";
import {
  showQuotaExceededNotification,
  showRateLimitErrorNotification,
  showRateLimitWarningNotification
} from "./analysistable/utils";
import ReanalyzeForm from "./reanalyze/reanalyze-form/ReanalyzeForm";
import api from "../../../services/api/index";
import { isLocaleAvailableForPlatform, poll } from "../../../utils";
import { show } from "../../../notifications/notifications";
import { enums, route } from "../../../shim/jsbridge";
import { areAdditionalAnalysisTypesAvailable } from "../../header/hooks/usePrepareReanalyzeData";
import { TEST_CONNECTION_MAP } from "../../integrations/consts";
import { NotificationsLevel } from "../../../lib/enums";
import { LoadingIndicator } from "../../../components";

// check tcbase/integrations/enums.py StatusCode
const QUEUED = 20000;
const QUOTA_EXCEEDED = 21500;
const RL_SANDBOX_POLL_INTERVAL = 10000;
const RL_SANDBOX_MAX_TRIES = -1; // indefinite
const TOO_MANY_RATE_LIMIT_FAILS = 21600;
const TOO_MANY_RATE_LIMIT_WARNING = 21700;

const QUOTA_RATE_LIMIT_CODES = [
  QUOTA_EXCEEDED,
  TOO_MANY_RATE_LIMIT_FAILS,
  TOO_MANY_RATE_LIMIT_WARNING
];

const INTEGRATIONS = {
  cape: "CAPE",
  cisco_secure_malware_analytics: "Cisco Secure Malware Analytics",
  cuckoo: "Cuckoo",
  fireeye: "Fireeye",
  joe: "Joe",
  vmray_tcbase: "VMRay"
};

export const getLocaleForPlatform = (locale, platform) =>
  isLocaleAvailableForPlatform(platform) ? locale : undefined;

const prepareDataForApi = (
  samples,
  values,
  dynamicAnalysisList,
  rlcsParams
) => {
  if (!values.rl_dynamic_analysis) {
    return {
      ...values,
      dynamic_analysis: dynamicAnalysisList,
      samples
    };
  }

  if (rlcsParams.interactive) {
    return {
      ...values,
      dynamic_analysis: dynamicAnalysisList,
      samples,
      rl_cloud_sandbox_interactive: rlcsParams.interactive,
      rl_cloud_sandbox_platform: rlcsParams.platform
    };
  }

  return {
    ...values,
    dynamic_analysis: dynamicAnalysisList,
    samples,
    rl_cloud_sandbox_platform: rlcsParams.platform,
    rl_cloud_sandbox_locale: getLocaleForPlatform(
      rlcsParams.locale,
      rlcsParams.platform
    ),
    rl_cloud_sandbox_geolocation: rlcsParams.geolocation
  };
};

const showIntegrationConnectionError = integrationKey => {
  show({
    level: NotificationsLevel.ERROR,
    message: `${INTEGRATIONS[integrationKey]} integration is currently unavailable. Try again in a few minutes or check with your admin if the integration is correctly configured.`
  });
};

const checkIntegrationConnectivity = async integrationKey => {
  const checkConnectionStatus = TEST_CONNECTION_MAP[integrationKey];
  try {
    await checkConnectionStatus();
  } catch (error) {
    // prevent submission only if we are sure that the integration API is unreachable
    if (error.data?.detail?.api) {
      showIntegrationConnectionError(integrationKey);
      return false;
    }
  }
  return true;
};

const getSuccessMessage = resultCount => {
  if (resultCount > 1 && resultCount <= 5) {
    return "Samples sent for reanalysis.";
  } else if (resultCount > 5) {
    return "Samples sent for reanalysis. Processing them can take a while, and the appliance performance may be impacted.";
  }
  return "Sample sent for reanalysis.";
};

export const INITIAL_RLCS_PARAMS = {
  platform: enums.platforms.WINDOWS_11,
  locale: enums.locales.EN_US,
  geolocation: enums.geolocations.US,
  interactive: false
};

export const INITIAL_ANALYSIS_SELECTIONS = {
  tc: false,
  ti_cloud: false,
  rl_auxiliary_analysis: false,
  rl_dynamic_analysis: false,
  cape: false,
  cisco_secure_malware_analytics: false,
  cuckoo: false,
  fireeye: false,
  joe: false,
  vmray_tcbase: false
};

class Reanalyze extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      values: this.props.initial
        ? {
            ...this.props.initial
          }
        : {
            ...INITIAL_ANALYSIS_SELECTIONS
          },
      rlcsParams: INITIAL_RLCS_PARAMS,
      samples: this.props.samples,
      isLoading: true,
      isSubmitted: false,
      errors: {}
    };
  }

  componentDidMount() {
    api
      .prepareReanalyze(this.state.samples.map(sample => sample.hash_value))
      .then(r => {
        const data = r.data;
        const values = { ...this.state.values };

        if (data.tc.checked) {
          values.tc = true;
        }

        if (data.ti_cloud.autoupload && data.ti_cloud.enabled) {
          values.ti_cloud = true;
        }

        if (!data.dynamic_analysis.rl_dynamic_analysis?.enabled) {
          values.rl_dynamic_analysis = false;
        }

        this.setState({
          popupWindowData: data,
          isLoading: false,
          values
        });
      })
      .catch(err => {
        this.props.onClose();
        if (err.data.detail && err.data.detail.fields) {
          this.setState({ errors: err.data.detail.fields });
        } else if (err.data.message) {
          show({
            level: "error",
            message: err.data.message
          });
        } else {
          show({
            level: "error",
            message:
              "Unexpected error while preparing the Reanalyze dialog. Please try again."
          });
        }
      });
  }

  onValues = values => {
    this.setState({ values });
  };

  onRlcsParamsChange = newRlcsParams => {
    this.setState({ rlcsParams: newRlcsParams });
  };

  get onSubmit() {
    return this._onSubmit;
  }

  set onSubmit(value) {
    this._onSubmit = value;
  }

  _onSubmit = async e => {
    this.setState({
      isSubmitted: true
    });
    e.preventDefault();

    const { values, rlcsParams, samples } = this.state ?? {};

    const { tc, ti_cloud, ...data } = values;

    const dynamicAnalysisList = Object.entries(data).reduce(
      (acc, [key, value]) => (value ? [...acc, key] : acc),
      []
    );

    // check API connectivity of selected integrations
    const selectedIntegrations = dynamicAnalysisList.filter(
      key => key in INTEGRATIONS
    );
    const connectionsChecks = selectedIntegrations.map(integrationKey =>
      checkIntegrationConnectivity(integrationKey)
    );
    const connectionResults = await Promise.all(connectionsChecks);

    // update the dynamic analysis list based on integrations availability
    selectedIntegrations.map(
      (integration, index) => (values[integration] = connectionResults[index])
    );
    const availableDynamicAnalysisList = dynamicAnalysisList.filter(
      key => values[key]
    );

    const apiValues = prepareDataForApi(
      samples,
      values,
      availableDynamicAnalysisList,
      rlcsParams
    );

    // in case all selected analyses are unavailable don't send anything for reanalysis
    if (!Object.values(apiValues).some(value => value === true)) {
      this.props.onClose();
      return;
    }

    return api
      .reanalyze(apiValues)
      .then(r => {
        this.props.onClose();
        const data = r.data;

        const successMessage = getSuccessMessage(data.result.length);
        show({
          level: "success",
          message: successMessage
        });

        if (this.props.callbackOnSuccess) {
          data.tc = this.state.values.tc; // refresh data in the grid only if tc analysis was checked
          this.props.callbackOnSuccess(data);
        }

        if (this.props.onReanalyzePopupWindowSubmit) {
          this.props.onReanalyzePopupWindowSubmit(apiValues);
        }

        return {
          requested_analysis: data.requested_analysis,
          time_sent_to_analysis: data.time_sent_to_analysis
        };
      })
      .catch(err => {
        this.props.onClose();
        if (err.data.detail && err.data.detail.fields) {
          if (err.data.detail.fields.non_field_errors) {
            show({
              level: "error",
              message: err.data.detail.fields.non_field_errors.join(" ")
            });
          }
          this.setState({ errors: err.data.detail.fields });
        } else if (err.data.message) {
          show({
            level: "error",
            message: err.data.message
          });
        } else {
          show({
            level: "error",
            message:
              "Oops! Something went wrong while preparing Reanalyze window. Please try again."
          });
        }
      })
      .then(response => {
        const {
          requested_analysis: {
            dynamic_analysis: requestedDynamicAnalysis = [],
            samples: samplesForAnalysis = []
          } = {},
          time_sent_to_analysis: timeSentToAnalysis = null
        } = response;
        const requestedHashes = samplesForAnalysis.map(
          ({ hash_value: hashValue }) => hashValue
        );
        if (
          requestedDynamicAnalysis.includes("rl_dynamic_analysis") &&
          requestedHashes.length
        ) {
          const sentToAnalysis = moment(timeSentToAnalysis);
          const isNewAnalysis = changed =>
            sentToAnalysis.isSameOrBefore(changed);

          const isRateLimitOrQuotaHit = item =>
            QUOTA_RATE_LIMIT_CODES.includes(item.status_code) &&
            isNewAnalysis(item.changed);

          // stop polling condition
          // if at least one status is QUOTA_EXCEEDED or every element has "known outcome" (not queued)
          const stop = ({ data = [] }) => {
            if (!data.length) return false;
            return (
              data.some(isRateLimitOrQuotaHit) ||
              data.every(
                el => el.status_code !== QUEUED && isNewAnalysis(el.changed)
              )
            );
          };

          poll(
            () =>
              api.rlSandboxAnalysisStatus({
                hash_values: requestedHashes
              }),
            stop,
            RL_SANDBOX_POLL_INTERVAL,
            RL_SANDBOX_MAX_TRIES
          ).then(({ data = [] }) => {
            const hasQuotaExceeded = data.some(
              el => el.status_code === QUOTA_EXCEEDED
            );
            const rateLimitError = data.some(
              el => el.status_code === TOO_MANY_RATE_LIMIT_FAILS
            );
            const rateLimitWarning = data.some(
              el => el.status_code === TOO_MANY_RATE_LIMIT_WARNING
            );
            if (hasQuotaExceeded) {
              showQuotaExceededNotification();
            }
            if (rateLimitError) {
              showRateLimitErrorNotification();
            }
            if (rateLimitWarning) {
              showRateLimitWarningNotification();
            }
          });
        }

        return response;
      })
      .then(response => {
        const {
          requested_analysis: { samples: samplesForAnalysis = [] } = {}
        } = response;

        if (rlcsParams.interactive) {
          samplesForAnalysis?.forEach(({ hash_value: hashValue }) =>
            window.open(
              route("rl_interactive_analysis_for_hash", [hashValue]),
              "_blank"
            )
          );
        }
      });
  };

  render() {
    const { onClose } = this.props;
    const { errors, values, popupWindowData, samples } = this.state;

    return (
      <Modal
        show
        onHide={onClose}
        size={
          !this.state.isLoading &&
          areAdditionalAnalysisTypesAvailable(popupWindowData)
            ? "lg"
            : undefined
        }
      >
        <Modal.Header closeButton>
          <Modal.Title>Reanalyze</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.isLoading ? (
            <LoadingIndicator data-testid="reanalyze_form_is_loading" />
          ) : (
            <ReanalyzeForm
              initial={values}
              values={values}
              errors={errors}
              displayWarningMessage={samples.length > 5}
              popupWindowData={popupWindowData}
              onCancel={onClose}
              onValues={this.onValues}
              onSubmit={this.onSubmit}
              onRlcsParamsChange={this.onRlcsParamsChange}
              rlcsParams={this.state.rlcsParams}
              showFilePasswordField={false}
              interactiveAnalysisDisabled={samples?.length > 1}
              isSubmitted={this.state.isSubmitted}
            />
          )}
        </Modal.Body>
      </Modal>
    );
  }
}

Reanalyze.propTypes = {
  initial: PropTypes.shape({
    hashValues: PropTypes.arrayOf(PropTypes.string),
    tc: PropTypes.bool,
    ti_cloud: PropTypes.bool,
    rl_auxiliary_analysis: PropTypes.bool,
    rl_dynamic_analysis: PropTypes.bool,
    cape: PropTypes.bool,
    cisco_secure_malware_analytics: PropTypes.bool,
    cuckoo: PropTypes.bool,
    fireeye: PropTypes.bool,
    joe: PropTypes.bool,
    vmray_tcbase: PropTypes.bool
  }),
  samples: PropTypes.arrayOf(
    PropTypes.shape({
      hash_value: PropTypes.string,
      source_id: PropTypes.number,
      relationship_id: PropTypes.number
    })
  ),
  callbackOnSuccess: PropTypes.func,
  onClose: PropTypes.func,
  onReanalyzePopupWindowSubmit: PropTypes.func
};

export default Reanalyze;
